// R5, R6, R7 (docs/build-briefs/R5-*.md, R6-*.md, R7-*.md; design/mockups/roles-r5-*.html to
// roles-r7-*.html; DEC-313). The words and the arithmetic behind the role management dialogs, the
// staged role editor and the Edit Employee Access tab, kept apart from the DOM so they can be tested
// without a browser. Nothing here touches the page, the network or a stored permission id: a person
// sees plain task names and plain settings, never an id.

import type {
  PermissionGrant,
  RoleChangeDirection,
  RoleHolderDto,
  UserPermissionRowDto,
} from "./api.ts";

export const GRANT_WORDS: Record<PermissionGrant, string> = {
  allowed: "Allowed",
  approval_required: "Needs manager approval",
  denied: "Not allowed",
};

export function grantWord(grant: PermissionGrant): string {
  return GRANT_WORDS[grant];
}

// ---------------------------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------------------------

/** Two letters for the round badge beside a person: first letters of the first two words. */
export function initials(displayName: string): string {
  const words = displayName
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length === 0) {
    return "?";
  }
  const first = words[0] ?? "";
  const second = words.length > 1 ? (words[words.length - 1] ?? "") : "";
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}

/** "Dana Cruz" becomes "Dana". Used in sentences ("What Dana can do"). */
export function firstName(displayName: string): string {
  const word = displayName.trim().split(/\s+/)[0] ?? "";
  return word.length === 0 ? "this person" : word;
}

export function peopleCountText(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/** The default name offered in the copy dialog. */
export function defaultCopyName(label: string): string {
  return `Copy of ${label}`;
}

/** Names offered as one-tap chips in the copy dialog, none of which is already a role. */
export function copyNameSuggestions(label: string, takenLabels: readonly string[]): string[] {
  const taken = new Set(takenLabels.map(normalizeName));
  const candidates = [`${label} copy`, `${label} 2`, `Weekend ${label}`, `${label} (new)`];
  return candidates.filter((candidate) => !taken.has(normalizeName(candidate))).slice(0, 3);
}

// ---------------------------------------------------------------------------------------------
// Options and the switch-off dialog
// ---------------------------------------------------------------------------------------------

export function holdersSentence(count: number): string {
  if (count === 0) {
    return "Nobody has this role.";
  }
  return count === 1 ? "1 person has this role." : `${count} people have this role.`;
}

export function switchOffHint(count: number): string {
  if (count === 0) {
    return "Nobody has this role, so nobody is affected.";
  }
  return count === 1
    ? "Move the person who has it to another role first."
    : `Move the ${count} people to another role first.`;
}

export interface MovePick {
  /** The role the person goes to; absent until chosen. */
  readonly destination: string | undefined;
}

/** How many people still have no destination. */
export function peopleLeftToMove(
  holders: readonly Pick<RoleHolderDto, "userId">[],
  picks: Readonly<Record<string, string | undefined>>,
): number {
  return holders.filter((holder) => picks[holder.userId] === undefined).length;
}

export function blockedBannerText(label: string, count: number): { bold: string; rest: string } {
  const bold =
    count === 1 ? `1 person still has ${label}.` : `${count} people still have ${label}.`;
  return {
    bold,
    rest: ` You cannot switch it off until ${
      count === 1 ? "they have" : "each one has"
    } another role. Moving ${count === 1 ? "them" : "them"} first means nobody is left with no access.`,
  };
}

export function readyBannerText(): { bold: string; rest: string } {
  return {
    bold: "Everyone is moved.",
    rest: " Nobody will lose access. Tap Move and switch off to finish.",
  };
}

export function holderNote(holder: RoleHolderDto, label: string): string {
  if (holder.otherRoles.length === 0) {
    return `Only has ${label}. Needs a new role.`;
  }
  const names = holder.otherRoles.map((role) => role.label);
  const list =
    names.length === 1
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
  return `Also has ${list}. Moving takes only ${label} away.`;
}

export function moveFooterText(
  remaining: number,
  total: number,
  everyoneGoesTo: string | undefined,
): { bold: string; rest: string } {
  if (remaining > 0) {
    return {
      bold: `Move ${remaining} more ${remaining === 1 ? "person" : "people"}`,
      rest: " to switch this off.",
    };
  }
  return everyoneGoesTo === undefined
    ? { bold: peopleCountText(total), rest: " will be moved." }
    : { bold: peopleCountText(total), rest: ` will be moved to ${everyoneGoesTo}.` };
}

// ---------------------------------------------------------------------------------------------
// The staged role editor and the confirmation
// ---------------------------------------------------------------------------------------------

export function unsavedText(count: number): string {
  return count === 1 ? "1 change not saved" : `${count} changes not saved`;
}

export const CHANGE_SYMBOLS: Record<RoleChangeDirection, string> = {
  widen: "+",
  approval: "!",
  narrow: "−",
};

export function changeLine(from: PermissionGrant, to: PermissionGrant): string {
  return `Was ${grantWord(from)}. Now ${grantWord(to)}.`;
}

export function saveBannerText(count: number): { bold: string; rest: string } {
  if (count === 0) {
    return {
      bold: "Nobody has this role right now.",
      rest: " The change applies to anyone given it later.",
    };
  }
  return {
    bold: `This changes access for ${peopleCountText(count)} right now.`,
    rest: " It applies on their next tap. They do not need to sign in again.",
  };
}

export function saveButtonText(count: number): string {
  return count === 0 ? "Save" : `Save for ${peopleCountText(count)}`;
}

/** The note about people whose personal setting still decides. Null when nobody has one. */
export function personalSettingsNote(
  people: readonly { displayName: string; taskLabels: readonly string[] }[],
): string | null {
  if (people.length === 0) {
    return null;
  }
  const names = people.map((person) => {
    const tasks = person.taskLabels;
    return `${person.displayName} already has a personal setting for ${
      tasks.length === 1 ? (tasks[0] ?? "") : `${tasks.length} of these tasks`
    }`;
  });
  return `People with a change set just for them keep it. ${peopleCountText(people.length)} ${
    people.length === 1 ? "is" : "are"
  } affected by that: ${names.join("; ")}.`;
}

/** How many chips show before the rest sit behind a tap. */
export const PEOPLE_CHIPS_VISIBLE = 6;

// ---------------------------------------------------------------------------------------------
// Add a role to a person (R6)
// ---------------------------------------------------------------------------------------------

export function addRoleIntro(
  name: string,
  count: number,
): { lead: string; bold: string; tail: string } {
  const who = firstName(name);
  if (count === 0) {
    return {
      lead: `${who} keeps everything they can do now. This role adds `,
      bold: "nothing new",
      tail: ".",
    };
  }
  return {
    lead: `${who} keeps everything they can do now. They will also be able to do these `,
    bold: count === 1 ? "1 more thing" : `${count} more things`,
    tail: ":",
  };
}

export function gainLine(
  now: PermissionGrant,
  withRole: PermissionGrant,
  roleLabel: string,
): string {
  return `Now: ${grantWord(now)}. With ${roleLabel}: ${grantWord(withRole)}.`;
}

export function personalWinsNote(
  name: string,
  wins: readonly { label: string; grant: PermissionGrant }[],
): { lead: string; bold: string; tail: string } | null {
  if (wins.length === 0) {
    return null;
  }
  const who = firstName(name);
  if (wins.length === 1) {
    const only = wins[0];
    return {
      lead: `${who} has 1 personal setting that still wins: `,
      bold: only?.label ?? "",
      tail: ` stays ${grantWord(only?.grant ?? "denied")}.`,
    };
  }
  return {
    lead: `${who} has ${wins.length} personal settings that still win: `,
    bold: wins.map((win) => win.label).join(", "),
    tail: " keep their own setting.",
  };
}

// ---------------------------------------------------------------------------------------------
// The Access tab (R7)
// ---------------------------------------------------------------------------------------------

/** Planned tasks are not built yet, so they are not counted as something a person can or cannot do. */
function countable(rows: readonly UserPermissionRowDto[]): UserPermissionRowDto[] {
  return rows.filter((row) => !row.planned);
}

export interface AccessCounts {
  alone: number;
  withApproval: number;
  notAllowed: number;
}

export function accessCounts(rows: readonly UserPermissionRowDto[]): AccessCounts {
  const counts: AccessCounts = { alone: 0, withApproval: 0, notAllowed: 0 };
  for (const row of countable(rows)) {
    if (row.effectiveGrant === "allowed") counts.alone += 1;
    else if (row.effectiveGrant === "approval_required") counts.withApproval += 1;
    else counts.notAllowed += 1;
  }
  return counts;
}

/** Tasks with a setting made just for this person, in catalog order. */
export function exceptionRows(rows: readonly UserPermissionRowDto[]): UserPermissionRowDto[] {
  return rows.filter((row) => row.override !== null);
}

/** "Lounger", "Lounger and Barback", "Lounger, Barback and Closer". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? "";
  }
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

export function roleNamesFor(
  row: Pick<UserPermissionRowDto, "roleGrantFrom">,
  labelOf: (role: string) => string,
): string {
  return joinNames(row.roleGrantFrom.map(labelOf));
}

/** The grey or accent badge on an access line: "From Lounger" or "Just for Dana". */
export function sourceBadge(
  row: UserPermissionRowDto,
  personName: string,
  labelOf: (role: string) => string,
): { text: string; individual: boolean } {
  if (row.override !== null) {
    return { text: `Just for ${firstName(personName)}`, individual: true };
  }
  const names = roleNamesFor(row, labelOf);
  return { text: names.length === 0 ? "From their role" : `From ${names}`, individual: false };
}

/** "Lounger says: Not allowed" on an exception card. */
export function roleSaysText(
  row: UserPermissionRowDto,
  labelOf: (role: string) => string,
  roleCount: number,
): string {
  const names = roleNamesFor(row, labelOf);
  if (names.length === 0) {
    return `Their ${roleCount === 1 ? "role says" : "roles say"}: ${grantWord(row.roleGrant)}`;
  }
  const verb = row.roleGrantFrom.length === 1 ? "says" : "say";
  return `${names} ${verb}: ${grantWord(row.roleGrant)}`;
}

export interface AreaSummary {
  group: string;
  groupLabel: string;
  /** Lines for tasks the person can do, with or without a manager. */
  lines: UserPermissionRowDto[];
  /** Short badge text for the area header, or null when nothing is allowed. */
  headline: { text: string; individual: boolean } | null;
}

/** Areas in catalog order, each listing what the person can do and where it comes from. */
export function areaSummaries(
  rows: readonly UserPermissionRowDto[],
  personName: string,
  labelOf: (role: string) => string,
): AreaSummary[] {
  const order: string[] = [];
  const byGroup = new Map<string, UserPermissionRowDto[]>();
  for (const row of countable(rows)) {
    if (!byGroup.has(row.group)) {
      order.push(row.group);
      byGroup.set(row.group, []);
    }
    byGroup.get(row.group)?.push(row);
  }
  return order.map((group) => {
    const groupRows = byGroup.get(group) ?? [];
    const lines = groupRows.filter((row) => row.effectiveGrant !== "denied");
    let headline: AreaSummary["headline"] = null;
    if (lines.length > 0) {
      const badges = lines.map((row) => sourceBadge(row, personName, labelOf));
      const individual = badges.filter((badge) => badge.individual).length;
      const sources = [...new Set(badges.filter((b) => !b.individual).map((b) => b.text))];
      if (individual === 0 && sources.length === 1) {
        headline = { text: sources[0] ?? "", individual: false };
      } else if (individual === lines.length) {
        headline = { text: `Just for ${firstName(personName)}`, individual: true };
      } else if (sources.length === 1) {
        headline = {
          text: `Mostly ${sources[0]?.replace(/^From /, "from ") ?? ""}`,
          individual: false,
        };
      } else {
        headline = { text: "From several roles", individual: false };
      }
    }
    return { group, groupLabel: groupRows[0]?.groupLabel ?? group, lines, headline };
  });
}

/** The choices an exception can pick: the three settings, minus the approval one where it does not exist. */
export function exceptionChoices(
  row: Pick<UserPermissionRowDto, "allowsApproval">,
): PermissionGrant[] {
  return row.allowsApproval ? ["allowed", "approval_required", "denied"] : ["allowed", "denied"];
}

/** Whether the acting person may hand out this setting, and the reason when they may not. */
export function exceptionChoiceBlock(
  row: Pick<UserPermissionRowDto, "actorMayGrant" | "actorMayModify">,
  choice: PermissionGrant,
): string | null {
  if (!row.actorMayModify) {
    return "You cannot change this, because this person has it and you do not.";
  }
  if (!row.actorMayGrant && choice !== "denied") {
    return "You cannot give this out, because you do not have it yourself.";
  }
  return null;
}

/** Save stays off until the choice differs from what the role says. */
export function exceptionSaveReady(
  row: Pick<UserPermissionRowDto, "roleGrant" | "override">,
  choice: PermissionGrant | undefined,
): boolean {
  return choice !== undefined && choice !== row.roleGrant;
}
